import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'

function Privacy() {
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  }

  const sections = [
    {
      title: '1. Information We Collect',
      content: [
        {
          subtitle: '1.1 LinkedIn Profile Data',
          text: 'When you use HireFlow to generate resumes, we temporarily access your LinkedIn profile information including your name, headline, work experience, education, skills, and other profile sections you choose to include. This data is processed locally in your browser and only sent to our servers for AI-powered resume generation.',
        },
        {
          subtitle: '1.2 Usage Data',
          text: 'We collect analytics data about how you use the extension, including features used, generation counts, and error logs. This helps us improve the service and provide better support.',
        },
        {
          subtitle: '1.3 Account Information',
          text: 'When you create an account or subscribe to paid plans, we collect your email address, payment information (processed securely by Paddle), and subscription details.',
        },
      ],
    },
    {
      title: '2. How We Use Your Information',
      content: [
        {
          subtitle: '2.1 Service Delivery',
          text: 'We use your LinkedIn data solely to generate professional resumes, job-matched CVs, and related features. Your data is processed securely and is not permanently stored unless you explicitly save it in our system.',
        },
        {
          subtitle: '2.2 Service Improvement',
          text: 'Usage analytics help us understand how users interact with HireFlow, identify bugs, and develop new features that benefit our community.',
        },
        {
          subtitle: '2.3 Communication',
          text: 'We may send you important service updates, feature announcements, and promotional emails (which you can opt out of at any time).',
        },
      ],
    },
    {
      title: '3. Data Storage and Security',
      content: [
        {
          subtitle: '3.1 Secure Infrastructure',
          text: 'All data is stored on secure servers with enterprise-grade encryption. We use industry-standard security practices including SSL/TLS encryption for data in transit and AES-256 encryption for data at rest.',
        },
        {
          subtitle: '3.2 Data Retention',
          text: 'Generated resumes and profile data are stored only as long as necessary to provide the service. You can delete your data at any time through your account settings. Account data is retained for 90 days after account deletion to comply with legal requirements.',
        },
        {
          subtitle: '3.3 Third-Party Services',
          text: 'We use trusted third-party services including Supabase (database), OpenAI (AI processing), and Paddle (payments). These providers comply with GDPR and maintain strict security standards.',
        },
      ],
    },
    {
      title: '4. Data Sharing and Disclosure',
      content: [
        {
          subtitle: '4.1 No Data Selling',
          text: 'We never sell, rent, or trade your personal information to third parties. Your data is yours, and we respect your privacy.',
        },
        {
          subtitle: '4.2 Service Providers',
          text: 'We share limited data with service providers who help us operate HireFlow (hosting, payment processing, analytics). These providers are contractually bound to protect your data.',
        },
        {
          subtitle: '4.3 Legal Requirements',
          text: 'We may disclose your information if required by law, court order, or to protect the rights and safety of HireFlow, our users, or others.',
        },
      ],
    },
    {
      title: '5. Your Rights and Choices',
      content: [
        {
          subtitle: '5.1 Access and Portability',
          text: 'You can access, export, or download your data at any time through your account dashboard.',
        },
        {
          subtitle: '5.2 Correction and Deletion',
          text: 'You can update or correct your information, or request permanent deletion of your account and all associated data.',
        },
        {
          subtitle: '5.3 Opt-Out',
          text: 'You can opt out of marketing communications while still receiving essential service updates. You can also disable analytics tracking in extension settings.',
        },
        {
          subtitle: '5.4 Data Portability',
          text: 'Upon request, we will provide your data in a machine-readable format for transfer to another service.',
        },
      ],
    },
    {
      title: '6. Cookies and Tracking',
      content: [
        {
          subtitle: '6.1 Essential Cookies',
          text: 'We use essential cookies to maintain your session, remember your preferences, and ensure the extension functions properly.',
        },
        {
          subtitle: '6.2 Analytics Cookies',
          text: 'We use analytics tools to understand usage patterns and improve the service. You can disable these in your browser settings.',
        },
        {
          subtitle: '6.3 Third-Party Cookies',
          text: 'Our payment processor and analytics providers may use their own cookies. Please review their privacy policies for details.',
        },
      ],
    },
    {
      title: '7. International Data Transfers',
      content: [
        {
          text: 'HireFlow is operated from the United States. If you are located outside the U.S., your information may be transferred to and processed in the United States or other countries where our service providers operate. We ensure appropriate safeguards are in place for international transfers in compliance with GDPR and other applicable laws.',
        },
      ],
    },
    {
      title: '8. Children\'s Privacy',
      content: [
        {
          text: 'HireFlow is not intended for users under 16 years of age. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us immediately.',
        },
      ],
    },
    {
      title: '9. Changes to This Privacy Policy',
      content: [
        {
          text: 'We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. We will notify you of significant changes via email or through the extension. Your continued use of HireFlow after changes constitutes acceptance of the updated policy.',
        },
      ],
    },
    {
      title: '10. Contact Us',
      content: [
        {
          text: 'If you have questions about this Privacy Policy or want to exercise your rights, please contact us:',
        },
        {
          text: 'Email: hireflowapp@gmail.com',
        },
        {
          text: 'For data protection inquiries (GDPR): hireflowapp@gmail.com',
        },
      ],
    },
  ]

  return (
    <>
      <Helmet>
        <title>Privacy Policy - HireFlow</title>
        <meta
          name="description"
          content="Learn how HireFlow collects, uses, and protects your personal information. We are committed to transparency and protecting your privacy."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 bg-gradient-to-br from-neutral-50 to-white">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="heading-xl mb-6">Privacy Policy</h1>
            <p className="text-lg text-neutral-600 mb-4">
              <strong>Effective Date:</strong> December 1, 2025
            </p>
            <p className="text-lg text-neutral-600">
              At HireFlow, we are committed to protecting your privacy and being transparent about how we collect, use, and safeguard your personal information. This Privacy Policy explains our practices regarding data collection and your rights as a user.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-12">
              {sections.map((section, index) => (
                <motion.div
                  key={index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: '-100px' }}
                  variants={fadeInUp}
                  transition={{ duration: 0.6 }}
                >
                  <h2 className="text-2xl font-bold text-neutral-900 mb-6">
                    {section.title}
                  </h2>
                  <div className="space-y-6">
                    {section.content.map((item, itemIndex) => (
                      <div key={itemIndex}>
                        {item.subtitle && (
                          <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                            {item.subtitle}
                          </h3>
                        )}
                        <p className="text-neutral-600 leading-relaxed">
                          {item.text}
                        </p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Summary Box */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
              className="mt-12 card p-8 bg-primary-50 border-primary-200"
            >
              <h3 className="text-xl font-bold text-neutral-900 mb-4">
                Privacy Summary
              </h3>
              <ul className="space-y-3 text-neutral-700">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>We collect only the data necessary to provide our services</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Your LinkedIn data is processed securely and not permanently stored without consent</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>We never sell your personal information to third parties</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>You can access, export, or delete your data at any time</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>We use enterprise-grade security to protect your information</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>We comply with GDPR, CCPA, and other privacy regulations</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  )
}

export default Privacy
